import { useState, useEffect, useCallback } from "react";
import styles from "./AdminForm.module.css";
import apiService from "../../../../services/api";
import { validateFile, createImagePreview, revokeImagePreview } from "../../../../utils/fileUpload";

export default function YearColorForm({ item, onSubmit, onCancel }) {
    const [formData, setFormData] = useState({
        year: new Date().getFullYear(),
        colorName: "",
        hex: "#000000",
        keyword: "",
        description: "",
        bannerImageFile: null,
        colorListImageFile: null,
        beautyImageFile: null,
        beautyDescription: "",
        fashionImageFile: null,
        fashionDescription: "",
        interiorImageFile: null,
        interiorDescription: ""
    });
    const [imagePreviews, setImagePreviews] = useState({
        bannerImage: null,
        colorListImage: null,
        beautyImage: null,
        fashionImage: null,
        interiorImage: null
    });
    const [fileErrors, setFileErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (item) {
            setFormData({
                year: item.year || new Date().getFullYear(),
                colorName: item.colorName || "",
                hex: item.hex || "#000000",
                keyword: item.keyword || "",
                description: item.description || "",
                bannerImageFile: null,
                colorListImageFile: null,
                beautyImageFile: null,
                beautyDescription: item.beautySection?.description || "",
                fashionImageFile: null,
                fashionDescription: item.fashionSection?.description || "",
                interiorImageFile: null,
                interiorDescription: item.interiorSection?.description || ""
            });
            
            // 기존 이미지 미리보기 설정
            setImagePreviews({
                bannerImage: item.bannerImage || null,
                colorListImage: item.colorListImage || null,
                beautyImage: item.beautySection?.image || null,
                fashionImage: item.fashionSection?.image || null,
                interiorImage: item.interiorSection?.image || null
            });
        }
    }, [item]);

    // 컴포넌트 언마운트 시 이미지 미리보기 정리
    useEffect(() => {
        return () => {
            Object.values(imagePreviews).forEach(preview => {
                if (preview && !preview.startsWith('http') && !preview.startsWith('/')) {
                    revokeImagePreview(preview);
                }
            });
        };
    }, [imagePreviews]);

    const handleImageChange = useCallback((field, e) => {
        const file = e.target.files[0];
        if (!file) return;

        const validation = validateFile(file);
        if (!validation.isValid) {
            setFileErrors(prev => ({ ...prev, [field]: validation.errors.join(', ') }));
            return;
        }

        setFileErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[field];
            return newErrors;
        });
        
        setFormData(prev => ({ ...prev, [`${field}File`]: file }));
        
        const preview = createImagePreview(file);
        setImagePreviews(prev => ({ ...prev, [field]: preview }));
    }, []);

    const handleSubmit = useCallback(async (e) => {
        e.preventDefault();
        
        if (isSubmitting) {
            return;
        }

        setIsSubmitting(true);
        
        try {
            console.log('=== SUBMIT YEAR COLOR ===');
            console.log('Item:', item);
            console.log('Form data:', formData);
            
            if (item && item.year) {
                // 수정 모드: year는 URL 파라미터로만 전달
                const updateData = {
                    colorName: formData.colorName,
                    hex: formData.hex,
                    keyword: formData.keyword,
                    description: formData.description,
                    beautyDescription: formData.beautyDescription,
                    fashionDescription: formData.fashionDescription,
                    interiorDescription: formData.interiorDescription
                };
                
                // 파일이 있는 경우만 추가
                if (formData.bannerImageFile) {
                    updateData.bannerImage = formData.bannerImageFile;
                }
                if (formData.colorListImageFile) {
                    updateData.colorListImage = formData.colorListImageFile;
                }
                if (formData.beautyImageFile) {
                    updateData.beautyImage = formData.beautyImageFile;
                }
                if (formData.fashionImageFile) {
                    updateData.fashionImage = formData.fashionImageFile;
                }
                if (formData.interiorImageFile) {
                    updateData.interiorImage = formData.interiorImageFile;
                }
                
                console.log('Update data:', updateData);
                await apiService.updateYearColor(item.year, updateData);
            } else {
                // 생성 모드: year 포함
                const createData = {
                    year: formData.year,
                    colorName: formData.colorName,
                    hex: formData.hex,
                    keyword: formData.keyword,
                    description: formData.description,
                    beautyDescription: formData.beautyDescription,
                    fashionDescription: formData.fashionDescription,
                    interiorDescription: formData.interiorDescription
                };
                
                // 파일이 있는 경우만 추가
                if (formData.bannerImageFile) {
                    createData.bannerImage = formData.bannerImageFile;
                }
                if (formData.colorListImageFile) {
                    createData.colorListImage = formData.colorListImageFile;
                }
                if (formData.beautyImageFile) {
                    createData.beautyImage = formData.beautyImageFile;
                }
                if (formData.fashionImageFile) {
                    createData.fashionImage = formData.fashionImageFile;
                }
                if (formData.interiorImageFile) {
                    createData.interiorImage = formData.interiorImageFile;
                }
                
                console.log('Create data:', createData);
                await apiService.createYearColor(createData);
            }
            
            alert("저장되었습니다.");
            onSubmit();
        } catch (error) {
            console.error('Form submission error:', error);
            console.error('Error details:', {
                message: error.message,
                stack: error.stack
            });
            alert(`저장에 실패했습니다: ${error.message || '알 수 없는 오류'}`);
        } finally {
            setIsSubmitting(false);
        }
    }, [formData, item, onSubmit, isSubmitting]);

    const handleCancel = useCallback(() => {
        if (isSubmitting) return;
        onCancel();
    }, [onCancel, isSubmitting]);

    return (
        <form className={styles.formWrap} onSubmit={handleSubmit}>
            <h2 className={styles.title}>
                {item ? `${item.year}년 올해의 컬러 수정` : "올해의 컬러 등록"}
            </h2>
            
            <label className={styles.label}>
                연도
                <input 
                    type="number" 
                    className={styles.input} 
                    value={formData.year}
                    onChange={(e) => setFormData(prev => ({...prev, year: parseInt(e.target.value)}))}
                    required 
                    disabled={isSubmitting || !!item}
                    min="2000"
                    max="2100"
                />
            </label>

            <label className={styles.label}>
                컬러 이름
                <input 
                    type="text" 
                    className={styles.input} 
                    value={formData.colorName}
                    onChange={(e) => setFormData(prev => ({...prev, colorName: e.target.value}))}
                    required 
                    disabled={isSubmitting}
                />
            </label>

            <label className={styles.label}>
                HEX 색상
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <input 
                        type="color" 
                        value={formData.hex}
                        onChange={(e) => setFormData(prev => ({...prev, hex: e.target.value}))}
                        disabled={isSubmitting}
                        style={{ width: '60px', height: '40px' }}
                    />
                    <input 
                        type="text" 
                        className={styles.input} 
                        value={formData.hex}
                        onChange={(e) => setFormData(prev => ({...prev, hex: e.target.value}))}
                        required 
                        disabled={isSubmitting}
                        style={{ flex: 1 }}
                    />
                </div>
            </label>

            <label className={styles.label}>
                키워드
                <input 
                    type="text" 
                    className={styles.input} 
                    value={formData.keyword}
                    onChange={(e) => setFormData(prev => ({...prev, keyword: e.target.value}))}
                    required 
                    disabled={isSubmitting}
                    placeholder="예: 포용, 융합, 특별함"
                />
            </label>

            <label className={styles.label}>
                설명
                <textarea 
                    className={styles.textarea} 
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({...prev, description: e.target.value}))}
                    required 
                    disabled={isSubmitting}
                    rows={5}
                />
            </label>

            <label className={styles.label}>
                배너 이미지
                <div className={styles.imageUpload}>
                    <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageChange('bannerImage', e)}
                        disabled={isSubmitting}
                    />
                    <div className={styles.imageUploadIcon}>📷</div>
                    <div className={styles.imageUploadText}>이미지를 선택하세요</div>
                </div>
                {fileErrors.bannerImage && <div className={styles.error}>{fileErrors.bannerImage}</div>}
                {imagePreviews.bannerImage && (
                    <div className={styles.imagePreview}>
                        <img src={imagePreviews.bannerImage} alt="배너 미리보기" />
                    </div>
                )}
            </label>

            <label className={styles.label}>
                컬러 리스트 이미지
                <div className={styles.imageUpload}>
                    <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageChange('colorListImage', e)}
                        disabled={isSubmitting}
                    />
                    <div className={styles.imageUploadIcon}>📷</div>
                    <div className={styles.imageUploadText}>이미지를 선택하세요</div>
                </div>
                {fileErrors.colorListImage && <div className={styles.error}>{fileErrors.colorListImage}</div>}
                {imagePreviews.colorListImage && (
                    <div className={styles.imagePreview}>
                        <img src={imagePreviews.colorListImage} alt="컬러 리스트 미리보기" />
                    </div>
                )}
            </label>

            <h3 style={{ marginTop: '30px', marginBottom: '15px' }}>뷰티 섹션</h3>
            <label className={styles.label}>
                뷰티 이미지
                <div className={styles.imageUpload}>
                    <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageChange('beautyImage', e)}
                        disabled={isSubmitting}
                    />
                    <div className={styles.imageUploadIcon}>📷</div>
                    <div className={styles.imageUploadText}>이미지를 선택하세요</div>
                </div>
                {fileErrors.beautyImage && <div className={styles.error}>{fileErrors.beautyImage}</div>}
                {imagePreviews.beautyImage && (
                    <div className={styles.imagePreview}>
                        <img src={imagePreviews.beautyImage} alt="뷰티 미리보기" />
                    </div>
                )}
            </label>
            <label className={styles.label}>
                뷰티 설명
                <textarea 
                    className={styles.textarea} 
                    value={formData.beautyDescription}
                    onChange={(e) => setFormData(prev => ({...prev, beautyDescription: e.target.value}))}
                    disabled={isSubmitting}
                    rows={3}
                />
            </label>

            <h3 style={{ marginTop: '30px', marginBottom: '15px' }}>패션 섹션</h3>
            <label className={styles.label}>
                패션 이미지
                <div className={styles.imageUpload}>
                    <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageChange('fashionImage', e)}
                        disabled={isSubmitting}
                    />
                    <div className={styles.imageUploadIcon}>📷</div>
                    <div className={styles.imageUploadText}>이미지를 선택하세요</div>
                </div>
                {fileErrors.fashionImage && <div className={styles.error}>{fileErrors.fashionImage}</div>}
                {imagePreviews.fashionImage && (
                    <div className={styles.imagePreview}>
                        <img src={imagePreviews.fashionImage} alt="패션 미리보기" />
                    </div>
                )}
            </label>
            <label className={styles.label}>
                패션 설명
                <textarea 
                    className={styles.textarea} 
                    value={formData.fashionDescription}
                    onChange={(e) => setFormData(prev => ({...prev, fashionDescription: e.target.value}))}
                    disabled={isSubmitting}
                    rows={3}
                />
            </label>

            <h3 style={{ marginTop: '30px', marginBottom: '15px' }}>인테리어 섹션</h3>
            <label className={styles.label}>
                인테리어 이미지
                <div className={styles.imageUpload}>
                    <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageChange('interiorImage', e)}
                        disabled={isSubmitting}
                    />
                    <div className={styles.imageUploadIcon}>📷</div>
                    <div className={styles.imageUploadText}>이미지를 선택하세요</div>
                </div>
                {fileErrors.interiorImage && <div className={styles.error}>{fileErrors.interiorImage}</div>}
                {imagePreviews.interiorImage && (
                    <div className={styles.imagePreview}>
                        <img src={imagePreviews.interiorImage} alt="인테리어 미리보기" />
                    </div>
                )}
            </label>
            <label className={styles.label}>
                인테리어 설명
                <textarea 
                    className={styles.textarea} 
                    value={formData.interiorDescription}
                    onChange={(e) => setFormData(prev => ({...prev, interiorDescription: e.target.value}))}
                    disabled={isSubmitting}
                    rows={3}
                />
            </label>
            
            <div className={styles.buttonGroup}>
                <button 
                    type="submit" 
                    className={styles.submitBtn}
                    disabled={isSubmitting}
                >
                    {isSubmitting ? "처리중..." : "저장"}
                </button>
                <button 
                    type="button" 
                    onClick={handleCancel} 
                    className={styles.cancelBtn}
                    disabled={isSubmitting}
                >
                    취소
                </button>
            </div>
        </form>
    );
}

